/**
 * ShippingLabelModal — print-only A5 landscape shipping label.
 *
 * 2026-02-19 — Added for Purchase Orders (PO detail drawer) and
 * Service & Repair tickets (vendor-bound stages). Pulls the FROM
 * (branch) + TO (vendor) addresses so the clinic can cut-and-paste
 * the label onto a courier package.
 *
 * Zero business-logic changes: this is a presentation-only overlay.
 * The parent fetches `branch` + `vendor` (and clinicName) once and
 * passes them in. We don't touch PO, Service, Vendor or Branch APIs.
 *
 * Guards:
 *   • If TO (vendor) address is incomplete → red warning + "Fix vendor".
 *   • If FROM (branch) address is incomplete → amber warning + "Fix branch".
 *   • Soft-warn only — "Print anyway" is always offered (handwritten fallback).
 *
 * Print CSS is mounted inline so the global stylesheet stays untouched.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Printer, X, AlertTriangle, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import useEscapeKey from '../hooks/useEscapeKey';

/** Returns the list of missing critical fields on an address record. */
function missingAddressFields(entity) {
  if (!entity) return ['record'];
  const miss = [];
  if (!entity.address?.toString().trim()) miss.push('address');
  if (!entity.city?.toString().trim()) miss.push('city');
  if (!entity.state?.toString().trim()) miss.push('state');
  // Vendor doesn't carry pincode today; only flag it for branch.
  if ('pincode' in entity && !entity.pincode?.toString().trim()) miss.push('pincode');
  if (!entity.phone?.toString().trim()) miss.push('phone');
  return miss;
}

/** Formats a multi-line address block (null-safe). */
function fmtAddress({ address, city, state, pincode }) {
  const line2 = [city, state, pincode].filter(Boolean).join(', ');
  return [address, line2].filter(Boolean);
}

export default function ShippingLabelModal({
  // FROM
  clinicName,
  branch,                 // { name, address, city, state, pincode, phone, branch_id }
  // TO
  vendor,                 // { name, address, city, state, phone, vendor_id, contact_person }
  // Centre strip
  referenceLabel,         // "PO Number" | "Service Job Number"
  referenceNo,            // actual string
  date,                   // ISO string or display string
  // Navigation
  vendorFixHref = '/ha/vendors',
  branchFixHref = '/settings',
  // Lifecycle
  onClose,
}) {
  useEscapeKey(onClose);

  const [itemCount, setItemCount] = useState(1);
  const [proceedAnyway, setProceedAnyway] = useState(false);

  useEffect(() => {
    document.body.classList.add('printing-shipping-label');
    return () => document.body.classList.remove('printing-shipping-label');
  }, []);

  const branchMissing = useMemo(() => missingAddressFields(branch), [branch]);
  const vendorMissing = useMemo(() => missingAddressFields(vendor), [vendor]);
  const hasAnyWarning = branchMissing.length > 0 || vendorMissing.length > 0;

  const prettyDate = useMemo(() => {
    if (!date) return new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    try {
      return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return String(date); }
  }, [date]);

  const fromLines = useMemo(() => fmtAddress(branch || {}), [branch]);
  const toLines = useMemo(() => fmtAddress(vendor || {}), [vendor]);

  const showSheet = !hasAnyWarning || proceedAnyway;

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/60 flex items-start justify-center p-4 overflow-auto shipping-label-root"
      data-testid="shipping-label-modal"
    >
      <div className="bg-white shadow-2xl w-full max-w-[900px] shipping-label-sheet">
        {/* Toolbar — hidden in print */}
        <header className="flex items-center justify-between px-4 py-2 border-b border-slate-200 no-print">
          <div className="text-sm font-bold text-slate-700">Shipping Label · Print Preview (A5 landscape)</div>
          <div className="flex items-center gap-2">
            {showSheet && (
              <button
                onClick={() => window.print()}
                data-testid="shipping-label-print-btn"
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded shadow-sm">
                <Printer size={12} /> Print
              </button>
            )}
            <button
              onClick={onClose}
              data-testid="shipping-label-close"
              className="w-7 h-7 rounded hover:bg-slate-100 flex items-center justify-center text-slate-500">
              <X size={14} />
            </button>
          </div>
        </header>

        {/* Pre-print warnings */}
        {hasAnyWarning && !proceedAnyway && (
          <div className="p-5 space-y-3 no-print" data-testid="shipping-label-warnings">
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded p-3">
              <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
              <div className="text-xs text-amber-900 leading-relaxed">
                <div className="font-bold mb-1">Address information is incomplete.</div>
                <div>The label will print blank in some sections. We recommend updating the record first so couriers can deliver without manual intervention.</div>
              </div>
            </div>

            {vendorMissing.length > 0 && (
              <div className="border border-rose-200 bg-rose-50 rounded p-3" data-testid="shipping-label-vendor-warning">
                <div className="text-[11px] uppercase tracking-wider font-bold text-rose-800 mb-1">Vendor (TO) · {vendor?.name || 'Unknown'}</div>
                <div className="text-xs text-rose-900 mb-2">
                  Missing: <b>{vendorMissing.join(', ')}</b>
                </div>
                <Link
                  to={vendorFixHref}
                  onClick={onClose}
                  data-testid="shipping-label-fix-vendor"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-rose-700 hover:text-rose-900 underline">
                  <ExternalLink size={11} /> Update vendor profile
                </Link>
              </div>
            )}

            {branchMissing.length > 0 && (
              <div className="border border-amber-200 bg-amber-50 rounded p-3" data-testid="shipping-label-branch-warning">
                <div className="text-[11px] uppercase tracking-wider font-bold text-amber-800 mb-1">Branch (FROM) · {branch?.name || 'Unknown'}</div>
                <div className="text-xs text-amber-900 mb-2">
                  Missing: <b>{branchMissing.join(', ')}</b>
                </div>
                <Link
                  to={branchFixHref}
                  onClick={onClose}
                  data-testid="shipping-label-fix-branch"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 hover:text-amber-950 underline">
                  <ExternalLink size={11} /> Update branch settings
                </Link>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={onClose}
                className="px-3 py-1.5 text-xs font-semibold border border-slate-300 rounded hover:bg-slate-50">
                Cancel
              </button>
              <button
                onClick={() => setProceedAnyway(true)}
                data-testid="shipping-label-print-anyway"
                className="px-3 py-1.5 text-xs font-semibold bg-slate-700 hover:bg-slate-800 text-white rounded">
                Print anyway (handwrite missing fields)
              </button>
            </div>
          </div>
        )}

        {/* The actual label */}
        {showSheet && (
          <div className="shipping-label-page" data-testid="shipping-label-sheet">
            {/* Editable item-count row — hidden in print */}
            <div className="no-print px-5 py-2 border-b border-slate-100 flex items-center gap-3 text-xs">
              <label className="font-semibold text-slate-600">No. of items / pieces:</label>
              <input
                type="number"
                min={1}
                value={itemCount}
                onChange={(e) => setItemCount(Math.max(1, Number(e.target.value) || 1))}
                data-testid="shipping-label-item-count"
                className="w-20 border border-slate-300 rounded px-2 py-1 text-sm font-mono"
              />
              <span className="text-slate-400">(editable before printing)</span>
            </div>

            {/* ===== PRINTABLE LABEL SHEET ===== */}
            <div className="shipping-label-canvas">
              <div className="shipping-label-border">
                {/* FROM */}
                <div className="shipping-from">
                  <div className="shipping-tag">FROM</div>
                  <div className="shipping-from-name">{clinicName || 'Audinexa Clinic'}</div>
                  {branch?.name && <div className="shipping-from-sub">{branch.name}</div>}
                  {fromLines.map((ln, i) => (
                    <div key={`f-${i}`} className="shipping-from-line">{ln}</div>
                  ))}
                  {branch?.phone && <div className="shipping-from-line">Phone: {branch.phone}</div>}
                </div>

                {/* CENTRE STRIP */}
                <div className="shipping-centre">
                  <div className="shipping-centre-row">
                    <span className="shipping-centre-label">{referenceLabel || 'Reference'}:</span>
                    <span className="shipping-centre-value" data-testid="shipping-label-ref-no">{referenceNo || '—'}</span>
                  </div>
                  <div className="shipping-centre-row">
                    <span className="shipping-centre-label">Date:</span>
                    <span className="shipping-centre-value">{prettyDate}</span>
                  </div>
                  <div className="shipping-centre-row">
                    <span className="shipping-centre-label">No. of items:</span>
                    <span className="shipping-centre-value" data-testid="shipping-label-items-print">{itemCount}</span>
                  </div>
                </div>

                {/* TO (large & bold — delivery address is king) */}
                <div className="shipping-to">
                  <div className="shipping-tag shipping-tag-to">TO</div>
                  <div className="shipping-to-name" data-testid="shipping-label-vendor-name">{vendor?.name || '—'}</div>
                  {vendor?.contact_person && (
                    <div className="shipping-to-contact">Attn: {vendor.contact_person}</div>
                  )}
                  {toLines.map((ln, i) => (
                    <div key={`t-${i}`} className="shipping-to-line">{ln}</div>
                  ))}
                  {vendor?.phone && (
                    <div className="shipping-to-phone">Phone: {vendor.phone}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Scoped CSS — only active while this modal is mounted. */}
      <style>{`
        .shipping-label-canvas {
          padding: 20px;
          background: #f8fafc;
        }
        .shipping-label-border {
          border: 2px dashed #0f172a;
          padding: 18px 22px;
          display: grid;
          grid-template-columns: 1fr 180px 1.4fr;
          gap: 18px;
          background: #ffffff;
          min-height: 320px;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .shipping-tag {
          display: inline-block;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 2px;
          background: #0f172a;
          color: #ffffff;
          padding: 2px 10px;
          border-radius: 2px;
          margin-bottom: 10px;
        }
        .shipping-tag-to {
          background: #991b1b;
        }
        .shipping-from { font-size: 12px; color: #1e293b; }
        .shipping-from-name { font-size: 15px; font-weight: 800; margin-bottom: 2px; }
        .shipping-from-sub { font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 6px; }
        .shipping-from-line { font-size: 11px; line-height: 1.4; color: #334155; }

        .shipping-centre {
          border-left: 1px dashed #94a3b8;
          border-right: 1px dashed #94a3b8;
          padding: 0 14px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 10px;
        }
        .shipping-centre-row {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }
        .shipping-centre-label {
          font-size: 9px;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          color: #64748b;
          font-weight: 700;
        }
        .shipping-centre-value {
          font-size: 14px;
          font-weight: 700;
          color: #0f172a;
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        }

        .shipping-to {
          padding-left: 4px;
        }
        .shipping-to-name {
          font-size: 28px;
          font-weight: 900;
          line-height: 1.1;
          color: #0f172a;
          margin-bottom: 6px;
          letter-spacing: -0.5px;
        }
        .shipping-to-contact {
          font-size: 13px;
          font-weight: 700;
          color: #334155;
          margin-bottom: 6px;
        }
        .shipping-to-line {
          font-size: 16px;
          font-weight: 700;
          line-height: 1.35;
          color: #0f172a;
        }
        .shipping-to-phone {
          font-size: 16px;
          font-weight: 800;
          margin-top: 6px;
          color: #0f172a;
        }

        @media print {
          @page { size: A5 landscape; margin: 5mm; }
          body.printing-shipping-label > *:not(.shipping-label-root) { display: none !important; }
          .shipping-label-root {
            position: static !important;
            background: #ffffff !important;
            padding: 0 !important;
            overflow: visible !important;
          }
          .shipping-label-root .no-print { display: none !important; }
          .shipping-label-sheet {
            box-shadow: none !important;
            max-width: 100% !important;
          }
          .shipping-label-canvas {
            padding: 0 !important;
            background: #ffffff !important;
          }
          .shipping-label-border {
            min-height: calc(148mm - 20mm);
            page-break-inside: avoid;
          }
          /* Preserve bold black backgrounds (FROM/TO tags) in print. */
          .shipping-tag, .shipping-tag-to {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>
    </div>
  );
}
